/**
 * @file 顶部导航栏的数据
 * @author FengGuang(fengguang01@baidu.com)
 */
import UrlPattern from 'url-pattern';

export interface IMenuDataTagsItem {
    color: 'red' | 'green' | 'blue';
    text: string;
}

export interface IMenuDataItem {
    key: string;
    title: string;
    url?: string;
    urlPattern?: string | string[];
    urlPatternExclude?: string | string[];
    tags?: IMenuDataTagsItem[];
    target?: string | boolean;
    children?: IMenuDataItem[];
}

// 根据 url 设置唯一key
const setKeyFromObj = (item: IMenuDataItem[]): IMenuDataItem[] => {
    return item.map((subItem, index) => {
        if (!subItem.key && subItem.url) {
            subItem.key = subItem.url;
        }
        else if (!subItem.key && subItem.title) {
            subItem.key = `$${subItem.title}`;
        }
        else if (!subItem.key) {
            subItem.key = `$${index}`;
        }
        if (subItem.children) {
            subItem.children = setKeyFromObj(subItem.children);
        }
        return subItem;
    });
};

const menuData: IMenuDataItem[] = setKeyFromObj([
    {
        key: '',
        title: '安装',
        url: '/install/quick',
        urlPattern: '/install/**'
    },
    {
        key: '',
        title: '教程',
        url: '/tutorials',
        urlPattern: [
            '/tutorials',
            '/tutorials/**'
        ]
    },
    {
        key: '',
        title: '文档',
        url: '/documentation/docs/zh/guides/index_cn.html',
        urlPattern: '/documentation/docs/**',
        children: [
            {
                key: '',
                title: 'PaddlePaddle',
                url: '/documentation/docs/zh/guides/index_cn.html',
                urlPattern: '/documentation/docs/**'
            },
            {
                key: '',
                title: 'Paddle Lite',
                url: 'https://paddlelite.paddlepaddle.org.cn/introduction/tech_highlights.html',
                urlPattern: '/lite/**'
            },
            {
                key: '',
                title: 'Paddle Inference',
                url: 'https://paddleinference.paddlepaddle.org.cn/product_introduction/inference_intro.html',
                urlPattern: '/inference/**'
            }
        ]
    },
    {
        key: '',
        title: '模型库',
        url: '/modelbase',
        urlPattern: [
            '/modelbase',
            '/modelbasedetail',
            '/modelbasedetail/**'
        ],
        children: [
            {
                key: '',
                title: '热门模型库',
                url: 'https://www.paddlepaddle.org.cn/modelbase'
            },
            {
                key: '',
                title: '模型推理部署导航',
                url: 'https://www.paddlepaddle.org.cn/wholechain'
            },
            {
                key: '',
                title: '预训练模型库',
                url: 'https://www.paddlepaddle.org.cn/hublist'
            }
        ]
    },
    {
        key: '',
        title: '产品全景',
        url: '/overview',
        urlPattern: '/overview',
        children: [
            {
                key: '',
                title: '工具组件',
                children: [
                    {
                        key: '',
                        title: 'PaddleHub',
                        url: '/hub',
                        tags: [{ color: 'red', text: '热门' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleX',
                        url: '/paddle/paddleX',
                        tags: [{ color: 'green', text: '新品' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'VisualDL',
                        url: '/paddle/visualdl',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleCloud',
                        url: 'https://www.paddlepaddle.org.cn/paddle/paddlecloud',
                        tags: [{ color: 'blue', text: '邀测' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'AutoDL',
                        url: 'https://github.com/PaddlePaddle/AutoDL',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PARL',
                        url: 'https://github.com/PaddlePaddle/PARL',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PALM',
                        url: 'https://github.com/PaddlePaddle/PALM',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleFL',
                        url: 'https://github.com/PaddlePaddle/PaddleFL',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PGL',
                        url: 'https://github.com/PaddlePaddle/PGL',
                        target: true
                    },
                    {
                        key: '',
                        title: 'Paddle Quantum',
                        url: 'https://github.com/PaddlePaddle/Quantum',
                        tags: [{ color: 'green', text: '新品' }],
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '端到端开发套件',
                children: [
                    {
                        key: '',
                        title: 'PaddleGAN',
                        url: '/paddlegan',
                        tags: [{ color: 'green', text: '新品' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'ERNIE',
                        url: '/paddle/ernie',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleClas',
                        url: 'https://github.com/PaddlePaddle/PaddleClas',
                        tags: [{ color: 'green', text: '新品' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleDetection',
                        url: 'https://github.com/PaddlePaddle/PaddleDetection',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleSeg',
                        url: 'https://github.com/PaddlePaddle/PaddleSeg',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleOCR',
                        url: 'https://github.com/PaddlePaddle/PaddleOCR',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PLSC',
                        url: 'https://github.com/PaddlePaddle/PLSC',
                        target: true
                    },
                    {
                        key: '',
                        title: 'ElasticCTR',
                        url: 'https://github.com/PaddlePaddle/ElasticCTR',
                        target: true
                    },
                    {
                        key: '',
                        title: 'Parakeet',
                        url: 'https://github.com/PaddlePaddle/Parakeet',
                        tags: [{ color: 'green', text: '新品' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleHelix',
                        url: '/paddle/paddlehelix',
                        tags: [{ color: 'green', text: '新品' }],
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '基础模型库',
                children: [
                    {
                        key: '',
                        title: 'PaddleCV',
                        url: 'https://www.paddlepaddle.org.cn/paddle/paddlecv',
                        tags: [{ color: 'red', text: '热门' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleNLP',
                        url: 'https://www.paddlepaddle.org.cn/paddle/paddlenlp',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleRec',
                        url: 'https://github.com/PaddlePaddle/PaddleRec',
                        target: true
                    },
                    {
                        key: '',
                        title: 'PaddleSpeech',
                        url: 'https://github.com/PaddlePaddle/models#PaddleSpeech',
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '预测部署',
                children: [
                    {
                        key: '',
                        title: 'PaddleSlim',
                        url: 'https://github.com/PaddlePaddle/PaddleSlim',
                        target: true
                    },
                    {
                        key: '',
                        title: 'Paddle Inference',
                        url: 'https://www.paddlepaddle.org.cn/paddle/paddleinference',
                        target: true
                    },
                    {
                        key: '',
                        title: 'Paddle Serving',
                        url: 'https://github.com/PaddlePaddle/Serving',
                        target: true
                    },
                    {
                        key: '',
                        title: 'Paddle Lite',
                        url: 'https://www.paddlepaddle.org.cn/paddle/paddlelite',
                        tags: [{ color: 'red', text: '热门' }],
                        target: true
                    },
                    {
                        key: '',
                        title: 'Paddle.js',
                        url: 'https://www.paddlepaddle.org.cn/paddle/paddlejs',
                        tags: [{ color: 'blue', text: '邀测' }],
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '开发训练',
                children: [
                    {
                        key: '',
                        title: 'Paddle核心框架',
                        url: 'https://github.com/paddlepaddle/paddle',
                        target: true
                    },
                    {
                        key: '',
                        title: 'FleetAPI',
                        url: 'https://github.com/PaddlePaddle/Fleet',
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '',
                children: [
                    {
                        key: '',
                        title: 'EasyDL零门槛AI开发平台',
                        url: 'https://ai.baidu.com/easydl/',
                        target: true
                    },
                    {
                        key: '',
                        title: 'BML全功能AI开发平台',
                        url: 'https://cloud.baidu.com/product/bml.html',
                        target: true
                    },
                    {
                        key: '',
                        title: 'AI studio 学习与实训社区',
                        url: 'https://aistudio.baidu.com/',
                        target: true
                    }
                ]
            }
        ]
    },
    {
        key: '',
        title: '应用案例',
        url: '/customercase',
        urlPattern: '/customercase'
    },
    {
        key: '',
        title: '直播日历',
        url: '/live',
        urlPattern: '/live'
    },
    {
        key: '',
        title: '开发者社区',
        url: '/developercommunity',
        urlPattern: '/developercommunity',
        children: [
            {
                key: '',
                title: '开源共建',
                children: [
                    {
                        key: '',
                        title: '特殊兴趣小组（PPSIG）',
                        url: '/sig',
                        target: true
                    },
                    {
                        key: '',
                        title: '开源贡献指南',
                        url: '/contributionguide',
                        target: true
                    },
                    {
                        key: '',
                        title: '开源合作伙伴',
                        url: '/developercommunity?id=openSource',
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '组织&峰会',
                children: [
                    {
                        key: '',
                        title: '开发者技术专家（PPDE）',
                        url: '/ppde',
                        target: true
                    },
                    {
                        key: '',
                        title: '峰会（WAVE SUMMIT）',
                        url: 'https://www.wavesummit.com.cn/',
                        target: true
                    },
                    {
                        key: '',
                        title: '领航团',
                        url: '/ppdenavigategroup',
                        target: true
                    },
                    {
                        key: '',
                        title: '活动日历',
                        url: '/live',
                        target: true
                    }
                ]
            },
            // {
            //     key: '',
            //     title: '学术&治理',
            //     children: [
            //         {
            //             key: '',
            //             title: '学术研究',
            //             url: '/academicEcology',
            //             target: true
            //         },
            //         {
            //             key: '',
            //             title: '飞桨博士会',
            //             url: '/assocation',
            //             target: true
            //         },
            //         {
            //             key: '',
            //             title: '社区治理委员会',
            //             url: '/paddletechgroup',
            //             target: true
            //         }
            //     ]
            // },
            {
                key: '',
                title: '获取帮助',
                children: [
                    {
                        key: '',
                        title: '论坛',
                        url: 'https://aistudio.baidu.com/paddle/forum',
                        target: true
                    },
                    {
                        key: '',
                        title: 'GitHub',
                        url: 'https://github.com/PaddlePaddle/paddle',
                        target: true
                    },
                    {
                        key: '',
                        title: 'Gitee',
                        url: 'https://gitee.com/paddlepaddle/Paddle',
                        target: true
                    },
                    {
                        key: '',
                        title: '交流群',
                        url: 'https://www.paddlepaddle.org.cn/support/news?action=detail&id=2439',
                        target: true
                    }
                ]
            },
            {
                key: '',
                title: '',
                children: [
                    {
                        key: '',
                        title: 'AI Studio实训平台',
                        url: 'https://aistudio.baidu.com/aistudio/index',
                        target: true
                    },
                    {
                        key: '',
                        title: '社区资源',
                        url: '/developercommunity?id=communityResource',
                        target: true
                    }
                ]
            }
        ]
    }
]);


export const menuRightData: IMenuDataItem[] = setKeyFromObj([
    {
        key: '',
        title: '论坛',
        url: 'http://discuss.paddlepaddle.org.cn',
        // urlPattern: [
        //     '/support/news',
        //     '/support/news/**'
        // ],
        target: '_blank'
    },
    {
        key: '',
        title: 'GitHub',
        url: 'https://github.com/paddlepaddle/paddle',
        target: '_blank'
    }
]);


// 遍历元素找到当前页面 url 匹配的选项
export const findMenu = (
    theMenuData: IMenuDataItem[],
    targetUrl: string = window.location.href.slice(window.location.origin.length)
): IMenuDataItem[] => {
    let resItem: IMenuDataItem[] = [];
    theMenuData.some((item) => {
        // 先匹配 urlPatternExclude 如果匹配上说明这条不是我们需要的记录
        let urlPatternExclude: string[] = [];
        if (Array.isArray(item.urlPatternExclude)) {
            urlPatternExclude = item.urlPatternExclude;
        }
        else if (typeof (item.urlPatternExclude) === 'string') {
            urlPatternExclude = [item.urlPatternExclude];
        }
        const patternExcludeMatch = urlPatternExclude.some(
            urlpatt => !!(new UrlPattern(urlpatt).match(targetUrl))
        );
        if (patternExcludeMatch) {
            return false;
        }

        // 再匹配 urlExclude，如果匹配上说明这是我们需要的记录
        let urlPattern: string[] = [];
        if (Array.isArray(item.urlPattern)) {
            urlPattern = item.urlPattern;
        }
        else if (typeof (item.urlPattern) === 'string') {
            urlPattern = [item.urlPattern];
        }

        const patternMatch = urlPattern.some(urlpatt => {
            if (new UrlPattern(urlpatt).match(targetUrl)) {
                resItem.push(item);
                return true;
            }
            return false;
        });
        if (patternMatch) {
            return true;
        }

        // 如果都没匹配到，则去递归遍历子节点
        if (item.children) {
            const subResItem = findMenu(item.children, targetUrl);
            if (subResItem.length) {
                resItem.push(item);
                subResItem.forEach(childItem => {
                    resItem.push(childItem);
                });
                return true;
            }
        }
        return false;
    });
    return resItem;
};

export default menuData;
